/**
 * Phase 5.5.b — minimal toast store for transient UI affordances.
 *
 * Scope: transient status messages tied to chat-driven document
 * edits (e.g. "v{hash} criada (+N/−M)" after a `lx_doc_*` tool call
 * lands). Deliberately simple — no queueing priority, no actions on
 * toast, no animation library. A toast carries a stable id so
 * consumers can dismiss or replace specific entries; `addToast`
 * returns the id for that use case.
 *
 * Consumers:
 *   - `AIEditor` (5.5.b) — drops a toast on every `document.updated`
 *     event (bus: `useDocumentUpdatedStream`) showing version hash +
 *     patch stats.
 *   - Future phases — diff-view auto-switch (5.6), RASCUNHOS updates
 *     (5.7) can ride the same store.
 */

import { create } from "zustand";

export interface ToastEntry {
  id: string;
  /** Short text (≤80 char) — render verbatim. No localization layer
   *  yet; callers pass pre-localized strings. */
  text: string;
  /** Style hint; consumers map to color. `info` default. */
  kind?: "info" | "success" | "warn" | "error";
  /** Auto-dismiss delay in ms. Omit for a sticky toast (caller must
   *  dismiss manually). Default when omitted at the component layer
   *  is typically 4000ms. */
  autoDismissMs?: number;
  /** Optional single inline action (e.g. an "Undo" button — CCM copy/move
   *  §1.4). When present, `ToastOverlay` renders a button labelled
   *  `action.label`; clicking it runs `action.onAction()` then dismisses
   *  the toast. Callers pass a PRE-LOCALIZED label (no i18n layer in the
   *  store). Keep it to ONE action — this is a status toast, not a menu. */
  action?: {
    label: string;
    onAction: () => void;
  };
  /** Monotonic created-at timestamp; ordering is FIFO by this value. */
  createdAt: number;
}

interface ToastStoreState {
  toasts: ToastEntry[];
  addToast: (entry: Omit<ToastEntry, "id" | "createdAt">) => string;
  dismissToast: (id: string) => void;
  clearAll: () => void;
}

export const useToastStore = create<ToastStoreState>((set) => ({
  toasts: [],
  addToast: (entry) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    set((state) => ({
      toasts: [
        ...state.toasts,
        { ...entry, id, createdAt: Date.now() },
      ],
    }));
    return id;
  },
  dismissToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  clearAll: () => set({ toasts: [] }),
}));

// DEV-only accessor for Playwright assertions.
if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__toastStore", {
    value: useToastStore,
    writable: false,
    enumerable: false,
    configurable: true,
  });
}
